import React, { useState, useEffect, useCallback } from 'react';
import { Users as UsersIcon, Plus, Edit2, Trash2, Save, X, Shield, ShieldOff, Loader2 } from 'lucide-react';
import api from '../api';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // user being edited
  const [busy, setBusy] = useState(false);

  // form fields
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('staff');
  const [isActive, setIsActive] = useState(true);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.request('/api/users');
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) { alert('Failed to load users: ' + e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const resetForm = () => {
    setEmail(''); setFullName(''); setPassword(''); setRole('staff'); setIsActive(true);
    setEditing(null);
    setShowForm(false);
  };

  const startNew = () => {
    resetForm();
    setShowForm(true);
  };

  const startEdit = (u) => {
    setEditing(u);
    setEmail(u.email);
    setFullName(u.full_name);
    setPassword('');  // blank means don't change
    setRole(u.role);
    setIsActive(u.is_active);
    setShowForm(true);
  };

  const saveUser = async () => {
    if (!email.trim() || !fullName.trim()) {
      alert('Email and full name are required.');
      return;
    }
    if (!editing && password.length < 6) {
      alert('Password must be at least 6 characters.');
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        const body = { email, full_name: fullName, role, is_active: isActive };
        if (password.trim()) body.password = password;
        await api.request(`/api/users/${editing.id}`, {
          method: 'PUT', body: JSON.stringify(body),
        });
      } else {
        await api.request('/api/users', {
          method: 'POST',
          body: JSON.stringify({ email, full_name: fullName, password, role }),
        });
      }
      resetForm();
      await loadUsers();
    } catch (e) { alert('Failed: ' + e.message); }
    finally { setBusy(false); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete user "${u.email}"?\n\nTheir login credentials will no longer work and any active sessions will be terminated.`)) return;
    setBusy(true);
    try {
      await api.request(`/api/users/${u.id}`, { method: 'DELETE' });
      await loadUsers();
    } catch (e) { alert('Failed: ' + e.message); }
    finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    setBusy(true);
    try {
      await api.request(`/api/users/${u.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !u.is_active }),
      });
      await loadUsers();
    } catch (e) { alert('Failed: ' + e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="users-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UsersIcon className="h-6 w-6 text-blue-600" /> User Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Add, edit, or remove user accounts. Deleted users lose access immediately.</p>
        </div>
        <button onClick={startNew} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-testid="new-user-btn">
          <Plus className="h-4 w-4" /> New User
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="user-form">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">{editing ? `Edit ${editing.email}` : 'New User'}</h2>
            <button onClick={resetForm} className="p-1 text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="user-email-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="user-fullname-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {editing ? 'Password (leave blank to keep current)' : 'Password (min 6 chars)'}
              </label>
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={editing ? 'Leave blank to keep' : 'Min 6 characters'}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                data-testid="user-password-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="user-role-select"
              >
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {editing && (
              <div className="flex items-center gap-2 pt-5">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  data-testid="user-active-checkbox"
                />
                <label htmlFor="isActive" className="text-sm">Active (can log in)</label>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={resetForm} className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">Cancel</button>
            <button
              onClick={saveUser}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
              data-testid="save-user-btn"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              <Save className="h-4 w-4" /> {editing ? 'Save Changes' : 'Create User'}
            </button>
          </div>
        </div>
      )}

      {/* Users list */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Email</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Full Name</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Role</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Created</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="text-center py-8 text-gray-400">Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-8 text-gray-400">No users yet.</td></tr>
            ) : users.map(u => (
              <tr key={u.id} className="border-t hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium text-gray-900">{u.email}</td>
                <td className="px-4 py-3 text-gray-700">{u.full_name}</td>
                <td className="px-4 py-3">
                  {u.role === 'admin' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">
                      <Shield className="h-3 w-3" /> Admin
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">Staff</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.is_active ? (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Active</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">Inactive</span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button
                      onClick={() => startEdit(u)}
                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                      title="Edit credentials"
                      data-testid={`edit-user-${u.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => toggleActive(u)}
                      className={`p-1.5 rounded ${u.is_active ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}`}
                      title={u.is_active ? 'Deactivate' : 'Activate'}
                      data-testid={`toggle-user-${u.id}`}
                    >
                      {u.is_active ? <ShieldOff className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => deleteUser(u)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      title="Delete user"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
